import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "../../lib/utils";

interface ModalProps {
  title: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  headerSlot?: ReactNode;
  width?: string; // tailwind width class, default max-w-2xl
  maxHeight?: string;
  hideCloseButton?: boolean;
  preventDismiss?: boolean;
  bodyClassName?: string;
}

export function Modal({
  title,
  subtitle,
  onClose,
  children,
  footer,
  headerSlot,
  width = "max-w-2xl",
  maxHeight = "max-h-[90vh]",
  hideCloseButton = false,
  preventDismiss = false,
  bodyClassName,
}: ModalProps) {
  useEffect(() => {
    if (preventDismiss) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, preventDismiss]);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (!preventDismiss && e.target === e.currentTarget) {
          onClose();
        }
      }}
      // createPortal moves the DOM node to document.body, but React still
      // bubbles synthetic events along the *component* tree, not the DOM
      // tree -- so without this, a click anywhere in the modal (Cancel,
      // a list row, ...) keeps bubbling into whatever clickable element the
      // modal happens to be rendered inside of (e.g. an agent card's
      // navigate-on-click).
      onClick={(e) => e.stopPropagation()}
    >
      <div className={cn("flex w-full flex-col rounded-2xl border border-line bg-elevated shadow-2xl", width, maxHeight)}>
        <div className="flex items-start justify-between border-b border-line p-4 sm:p-5">
          <div>
            <h2 className="text-lg font-bold text-ink">{title}</h2>
            {subtitle && <div className="mt-0.5 text-sm text-ink-2">{subtitle}</div>}
          </div>
          {!hideCloseButton && (
            <button onClick={onClose} aria-label="Close" className="rounded-lg p-1.5 text-ink-3 hover:bg-soft hover:text-ink cursor-pointer">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {headerSlot}
        <div className={cn("flex-1 overflow-y-auto p-4 sm:p-5", bodyClassName)}>{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-line p-4 sm:p-5">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
